import { Box, Typography, useTheme, CircularProgress } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import DashboardPaper from "../../common/DashboardPaper";
import { formatAxisDate } from "../../../utils/chartDate";
import type { DeflectionsChartData } from "../../../types/deflections";

interface Props {
  data?: DeflectionsChartData;
  loading?: boolean;
}

export default function DeflectionsBarChart({ data, loading }: Props) {
  const theme = useTheme();
  const total = (data?.series?.[0]?.data ?? []).reduce((a, b) => a + b, 0);

  return (
    <DashboardPaper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box>
          <Typography variant="h6" fontWeight={600}>Deflections</Typography>
          <Typography variant="body2" color="text.secondary">
            Chargebacks blocked before they were raised.
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="caption" color="text.secondary">TOTAL</Typography>
          <Typography variant="h6" fontWeight={700}>{total.toLocaleString()}</Typography>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : data && total > 0 ? (
        <BarChart
          xAxis={[
            {
              data: data.xAxis,
              valueFormatter: (value: string, ctx: AxisValueFormatterContext) => formatAxisDate(value, ctx.location),
              tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
            },
          ]}
          yAxis={[{ tickLabelStyle: { fontSize: 12, fill: theme.palette.text.secondary } }]}
          series={data.series.map((s) => ({
            data: s.data,
            label: s.label,
            color: theme.palette.primary.main,
          }))}
          height={300}
          borderRadius={3}
          hideLegend
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}
    </DashboardPaper>
  );
}
